import {
  AsYouType,
  getExampleNumber,
  isPossiblePhoneNumber,
  isPossibleNumber,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';
import { CountryCode } from 'typescript/constants/localization';

/**
 * libphonenumber-js can throw instead of returning false (ParseError
 * 'TOO_SHORT' crashed the checkout payment page via the error boundary in
 * production; ParseError 'NOT_A_NUMBER' did the same on the delivery and
 * payment pages; non-string input throws a TypeError; unrecognized country
 * codes throw 'Unknown country'). Callers only use these for cosmetic hints
 * (maxlength, placeholder, as-you-type formatting) or validation, so
 * degrade gracefully instead of throwing.
 */

/**
 * Throw-safe wrapper around isPossiblePhoneNumber (expects a complete
 * phone number)
 * @returns false when the input is not a possible phone number or
 * libphonenumber-js throws
 */
export const safeIsPossiblePhoneNumber = (
  value: unknown,
  countryCode: CountryCode,
): boolean => {
  try {
    return (
      typeof value === 'string' && isPossiblePhoneNumber(value, countryCode)
    );
  } catch {
    return false;
  }
};

/**
 * Throw-safe wrapper around isPossibleNumber (accepts national /
 * partially-entered numbers)
 * @returns false when the input is not a possible phone number or
 * libphonenumber-js throws
 */
export const safeIsPossibleNumber = (
  value: unknown,
  countryCode: CountryCode,
): boolean => {
  try {
    return typeof value === 'string' && isPossibleNumber(value, countryCode);
  } catch {
    return false;
  }
};

/**
 * Throw-safe formatter for example phone numbers (used for placeholder text)
 * @returns a nationally formatted example number for the country, or undefined
 * when the country is unknown or libphonenumber-js throws
 */
export const safeFormatExampleNumber = (
  countryCode: CountryCode,
): string | undefined => {
  try {
    return getExampleNumber(countryCode, examples)?.formatNational();
  } catch {
    return undefined;
  }
};

/**
 * Throw-safe wrapper around AsYouType (formats a phone number as the user
 * types)
 * @returns the formatted phone number, or the raw input when
 * libphonenumber-js throws
 */
export const safeFormatAsYouType = (
  value: string,
  countryCode: CountryCode,
): string => {
  try {
    return new AsYouType(countryCode).input(value);
  } catch {
    return value;
  }
};
